<template>
  <div
    v-if="isCreditOfferEligible && showLoyaltyCcBanner"
    class="cc-component margin-bottom-m grid-container"
  >
    <!-- Shown until the shared banner emits its loaded event, then removed -->
    <CreditCardBannerSkeleton
      v-if="!isConsumableBannerLoaded"
      class="padding-top-m padding-bottom-m"
    />
    <!-- Mounted immediately (so it can fetch + emit) but only made visible once loaded. -->
    <CreditcardBannerConsumableCreditCardBanner
      v-show="isConsumableBannerLoaded"
      class="padding-top-m padding-bottom-m"
      source="loyaltyhub"
      :card-activation-enhancements-enabled="killswitchesStore?.cardActivationEnhancementsEnabled"
      :credit-new-account-discount-enabled="false"
      @credit-card-consumable-banner-loaded="handleConsumableBannerLoaded"
    />
  </div>
  <div
    v-else-if="isCreditOfferEligible"
    class="cc-component margin-bottom-m grid-container"
  >
    <div class="cell grid-x padding-top-xs padding-bottom-xs content-space">
      <div
        class="cell small-12 medium-2 large-4 content-align small-margin-bottom-s medium-margin-bottom-z"
        :class="isBcom ? 'mlarge-3' : 'mlarge-2'"
      >
        <span class="images-wrapper small-margin-right-z medium-margin-right-xs large-margin-right-xxxl mlarge-margin-right-s small-content-center">
          <img
            :src="isBcom ? blmCreditcardImg : macysCreditcardImg"
            alt="Credit Card"
          />
        </span>
      </div>
      <div
        class="cell grid-x small-12 medium-10 large-8 content-space"
        :class="isBcom ? 'mlarge-9' : 'mlarge-10'"
      >
        <div
          class="cell small-12 medium-9 large-12 mlarge-7 small-padding-right-z medium-padding-right-m large-padding-right-z"
          @click="handleClickToPointScroll($event, emit)"
        >
          <p
            class="margin-bottom-xxs"
            :class="isBcom ? 'h5' : 'h4'"
          >
            {{ creditHeaderDetail }}
          </p>
          <div
            v-if="isBcom && creditHeaderContent"
            class="margin-bottom-xxs body"
          >
            {{ creditHeaderContent }}
          </div>
          <div :class="isBcom ? 'margin-bottom-xxs text-color small' : 'body'">
            <span v-html="isBcom || !isMcomAssociated ? CREDITCARD_OFFER.APPROVAL_TEXT : creditHeaderContent"></span>&nbsp;
            <a
              href="javascript:void(0)"
              :class="isBcom ? 'link-sm' : 'link-body'"
              @click.stop="isBcom ? openModal('infoExclusionsModal', 'Info/Exclusions', 'infoExclusions') : openExclusions()"
              >{{ isMcomAssociated ? OFFER_INFO_EXCLUSIONS : INFO_EXCLUSIONS }}</a
            >
          </div>
          <div
            v-if="isBcom"
            class="small-margin-bottom-m body medium-margin-bottom-z large-margin-bottom-xs mlarge-margin-bottom-z"
          >
            {{ CREDITCARD_OFFER.HAVE_CARD }}
            <a
              class="link-body"
              :href="CREDITCARD_OFFER.ADD_TO_WALLET_LINK"
              >{{ CREDITCARD_OFFER.ADD_TO_WALLET }}</a
            >
          </div>
          <div
            v-else
            class="small-margin-bottom-m body medium-margin-bottom-z large-margin-bottom-s mlarge-margin-bottom-z margin-top-xs"
          >
            <h6
              class="margin-bottom-xxs"
              v-html="isMcomAssociated ? CREDITCARD_OFFER.ASSOCIATION_EARN_POINTS : CREDITCARD_OFFER.EARN_POINTS"
            ></h6>
            <p
              class="body"
              v-html="isMcomAssociated ? CREDITCARD_OFFER.ASSOCIATION_POINTS_DETAILS : CREDITCARD_OFFER.POINTS_DETAILS"
            ></p>
          </div>
        </div>
        <div class="cell grid-y small-12 medium-3 large-8 mlarge-3 body content-center">
          <div class="display-row">
            <a
              :class="[
                'primary button button-small expanded expanded-width margin-bottom-xs',
                'small-margin-right-z medium-margin-right-z large-margin-right-xs mlarge-margin-right-z',
              ]"
              :href="CREDITCARD_OFFER.APPLY_NOW_LINK"
            >
              {{ CREDITCARD_OFFER.APPLY_NOW }}
            </a>
            <a
              class="secondary button button-small expanded expanded-width"
              :href="CREDITCARD_OFFER.LEARN_MORE_LINK"
              target="_blank"
            >
              {{ CREDITCARD_OFFER.LEARN_MORE }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { brand, CREDITCARD_OFFER } from '../../config';
import { handleClickToPointScroll } from '../../utils/scrollToPointUtil';
import { loadOverlayComponent } from '../../utils/loadOverlayComponent';
import CreditCardBannerSkeleton from './CreditCardBannerSkeleton.vue';
import blmCreditcardImg from '../../assets/images/blm_creditcard.png';
import macysCreditcardImg from '../../assets/images/macys_creditcard.png';
import { useLoyaltyHubStore } from '../../stores/useLoyaltyHubStore.js';
import { useEnvPropsStore } from '../../stores/useEnvPropsStore';
import { useModalOverlayStore } from '../../stores/useModalOverlayStore';
import { useKillswitchesStore } from '../../stores/useKillswitchesStore';
const CreditcardBannerConsumableCreditCardBanner = defineAsyncComponent({
  loader: () => import('@nuxt-common/creditcard-banner-consumable/app/components/CreditCardBanner.vue'),
  onError: () => {
    handleConsumableBannerLoaded();
  },
});

const store = useLoyaltyHubStore();
const envPropsStore = useEnvPropsStore();
const modalOverlayStore = useModalOverlayStore();
const killswitchesStore = useKillswitchesStore();
const isBcom = brand === 'bcom';
const { creditDiscountPercent, creditOfferContent, CREDIT_HEADER, DISCOUNT_PERCENT, TOTAL_SAVINGS, INFO_EXCLUSIONS, OFFER_INFO_EXCLUSIONS } =
  CREDITCARD_OFFER;
const summary = computed(() => store.unifiedLoyalty?.points?.loyaltyPointsResponse?.loyaltyInfo || {});
const { tier, tenderDetails, associated } = summary.value;
const isAssociated = associated === false;
const isMcomAssociated = !isBcom && isAssociated;
const bccAvailable = tenderDetails?.bccAvailable || false;
const mccShow = (tier && tier.tierName === 'BRONZE') || isAssociated;
const isCreditOfferEligible = isBcom ? !bccAvailable : mccShow;
const showLoyaltyCcBanner = computed(
  () => killswitchesStore.cardActivationEnhancementsEnabled && killswitchesStore.cardActivationEnhancementsLoyaltyHubEnabled,
);

// Show the skeleton until the shared banner emits `credit-card-consumable-banner-loaded`
// (after its discount fetch settles), then reveal the banner.
const isConsumableBannerLoaded = ref(false);
const handleConsumableBannerLoaded = () => {
  isConsumableBannerLoaded.value = true;
};
const discountPercentage = computed(() => store.discountPercentage || DISCOUNT_PERCENT);
const creditHeaderContent = computed(() => creditOfferContent(discountPercentage.value, TOTAL_SAVINGS));
const creditHeaderDetail = computed(() => (isMcomAssociated ? creditDiscountPercent(discountPercentage.value) : CREDIT_HEADER));
const deviceType = computed(() => envPropsStore.headerContext?.deviceType || 'DESKTOP');

const openExclusions = () => {
  window.open(CREDITCARD_OFFER.INFO_EXCLUSIONS_LINK, '_blank');
};

const openModal = async (idVal, titleVal, componentPath) => {
  const component = await loadOverlayComponent(componentPath);
  await modalOverlayStore.setModalOverlay({
    width: '44.25rem',
    id: idVal,
    title: titleVal,
    component,
  });
  if (modalOverlayStore.showModal[idVal]) {
    modalOverlayStore.close(idVal);
  } else {
    modalOverlayStore.toggle(idVal);
  }
};

const emit = defineEmits(['scroll-to-exclusion-details']);
const discountDetails = () => store.fetchDiscountDetails({ deviceType: deviceType.value });

onMounted(async () => {
  // Calling dicount API to get the percentage for BCOM or Association page in both brands
  // Only call if PRD-1403 banner is NOT taking over
  if (!showLoyaltyCcBanner.value && (isMcomAssociated || isBcom)) {
    await discountDetails();
  }
});
</script>






<style lang="scss" scoped>
.content-space {
  justify-content: space-between;
}
.content-center {
  display: flex;
  justify-content: center;
}
@include breakpoint(small down) {
  .small-content-center {
    display: flex;
    justify-content: center;
  }
}
@include breakpoint(large only) {
  .display-row {
    display: flex;
    flex-flow: row;
  }
  .expanded-width {
    width: auto;
  }
}
@include breakpoint(medium only) {
  .content-align {
    display: flex;
    align-items: center;
  }
}
@include breakpoint(mlarge) {
  .display-row {
    display: block;
  }
}


.text-color {
  color: $gray-1-color;
}
@include breakpoint(small down) {
  .images-wrapper {
    img {
      width: rem-calc(163);
      height: rem-calc(92);
      flex-shrink: 0;
    }
  }
}
@include breakpoint(medium only) {
  .images-wrapper {
    img {
      width: rem-calc(108);
      height: rem-calc(76);
    }
  }
}
@include breakpoint(large only) {
  .images-wrapper {
    img {
      width: rem-calc(168);
      height: rem-calc(131);
      flex-shrink: 0;
    }
  }
}
@include breakpoint(mlarge) {
  .images-wrapper {
    img {
      width: rem-calc(220);
      height: rem-calc(147);
      flex-shrink: 0;
    }
  }
}
</style>